import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { ArrowRight } from 'lucide-react';

const BLOG_TEXTURE = 'https://static.prod-images.emergentagent.com/jobs/a6bba23e-9f76-4699-a363-7b1495992596/images/0e0695e1ed494a24ce1e2d072ebbd65c33e97dd6d2da8155759900d0bd20e3ea.png';

const posts = [
  {
    title: 'Why Nano Influencer Marketing Works Better Than Celebrity Campaigns for D2C Brands',
    category: 'Influencer Marketing',
    date: 'April 2026',
    excerpt: 'Most D2C brands waste their influencer marketing budgets on macro influencers with large but disengaged audiences. Here\'s why nano influencers — creators with 1,000 to 10,000 followers — consistently deliver better ROI, and how to run your first nano influencer campaign.',
    color: '#FF5C35',
    link: '/blog/nano-influencer-marketing',
  },
  {
    title: 'What Is AI UGC and Why D2C Brands Are Switching From Traditional Shoots',
    category: 'AI UGC',
    date: 'March 2026',
    excerpt: 'AI UGC is changing how D2C brands produce ad content. Traditional shoots are expensive, slow, and often outdated before they go live. Here\'s everything you need to know — costs, quality comparison, and when to use AI UGC vs real UGC.',
    color: '#F5F5F0',
    link: '/blog/ai-ugc-guide',
  },
  {
    title: 'Employer Branding in 2026: Why Growing Companies Are Investing in Content to Attract Talent',
    category: 'Employer Branding',
    date: 'March 2026',
    excerpt: 'Job seekers today do not just read job descriptions. They research companies on LinkedIn, Instagram, and Glassdoor before they apply. This post covers what employer branding content looks like in 2026 and how to build it without a big budget.',
    color: '#FF5C35',
    link: '/blog/employer-branding-2026',
  },
];

export default function BlogPage() {
  return (
    <>
      <SEOHead
        title="Marketing Blog for D2C Brands | Influencer Marketing & UGC Tips | Thriveit Media"
        description="The Thriveit Media blog covers influencer marketing, AI UGC, real UGC, and social media content tips for D2C brands."
        canonical="https://thriveitmedia.in/blog"
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Blog</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-4xl">
              Marketing Insights for D2C Brands
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Blog Grid */}
      <section data-testid="blog-grid" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <StaggerItem key={i}>
                <Link
                  to={post.link}
                  data-testid={`blog-post-${i}`}
                  className="block bg-[#111111] border border-white/5 group hover:border-[#FF5C35]/30 transition-all duration-500 hover:-translate-y-1 flex flex-col h-full"
                >
                  <div className="h-48 relative overflow-hidden">
                    <img
                      src={BLOG_TEXTURE}
                      alt={`Abstract texture for ${post.title}`}
                      className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111] to-transparent" />
                    <span
                      className="absolute top-4 left-4 text-xs uppercase tracking-[0.15em] font-heading font-bold px-3 py-1"
                      style={{ color: post.color, backgroundColor: `${post.color}15` }}
                    >
                      {post.category}
                    </span>
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <p className="text-xs font-body text-[#888880] mb-3">{post.date}</p>
                    <h2 className="font-heading text-lg font-bold text-[#F5F5F0] mb-3 leading-tight group-hover:text-[#FF5C35] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm font-body font-light text-[#888880] leading-relaxed mb-6 flex-1">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-sm font-body text-[#FF5C35] group-hover:gap-3 transition-all">
                      Read More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </>
  );
}
